import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Space, Tag, Typography, message, Segmented, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { mocService, MocHeader } from '../../services/moc.service';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  draft: 'default',
  submitted: 'processing',
  under_review: 'processing',
  risk_assessment_pending: 'orange',
  approval_pending: 'gold',
  approved_for_implementation: 'cyan',
  implementation_in_progress: 'blue',
  verification_pending: 'purple',
  closed: 'success',
  rejected: 'error',
  cancelled: 'default',
};

/** Matches backend `preset` query values (single-select views). */
const REGISTER_PRESETS = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Approval', value: 'pending_approval' },
  { label: 'Verification', value: 'pending_verification' },
  { label: 'Risk assess.', value: 'pending_risk' },
  { label: 'Implementation', value: 'implementation' },
  { label: 'High risk', value: 'high_risk' },
  { label: 'Cust. notify', value: 'customer_notification' },
] as const;

type ListingPreset = (typeof REGISTER_PRESETS)[number]['value'];

const VALID_PRESET = new Set(REGISTER_PRESETS.map((p) => p.value));

const MOCRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<MocHeader[]>([]);
  const [loading, setLoading] = useState(false);
  const [listingPreset, setListingPreset] = useState<ListingPreset>('all');
  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const presetParam = listingPreset === 'all' ? undefined : listingPreset;
      const list = await mocService.list({
        ...(presetParam ? { preset: presetParam } : {}),
        ...(appliedSearch.trim() ? { q: appliedSearch.trim() } : {}),
      });
      setRows(list);
    } catch (e: unknown) {
      console.error(e);
      message.error('Failed to load MOC register');
    } finally {
      setLoading(false);
    }
  }, [listingPreset, appliedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const p = searchParams.get('preset');
    if (p && VALID_PRESET.has(p as ListingPreset)) {
      setListingPreset(p as ListingPreset);
    }
  }, [searchParams]);

  const columns: ColumnsType<MocHeader> = [
    {
      title: 'MOC #',
      dataIndex: 'moc_number',
      key: 'moc_number',
      width: 160,
    },
    {
      title: 'Date',
      dataIndex: 'moc_date',
      key: 'moc_date',
      width: 120,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : ''),
    },
    {
      title: 'Title',
      dataIndex: 'change_title',
      key: 'change_title',
      ellipsis: true,
    },
    {
      title: 'Type',
      key: 'type',
      width: 180,
      ellipsis: true,
      render: (_, r) => r.changeType?.name ?? '—',
    },
    {
      title: 'Risk',
      key: 'risk_summary_score',
      width: 72,
      align: 'right',
      render: (_, r) =>
        r.risk_summary_score != null ? (
          <Tag color={r.risk_summary_score >= 12 ? 'red' : r.risk_summary_score >= 6 ? 'orange' : 'default'}>
            {r.risk_summary_score}
          </Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'Cust.',
      key: 'customer_notification_required',
      width: 72,
      align: 'center',
      render: (_, r) =>
        r.customer_notification_required ? <Tag color="blue">Yes</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (s: string) => (
        <Tag color={statusColors[s] || 'default'}>{s.replace(/_/g, ' ')}</Tag>
      ),
    },
    {
      title: 'Urgency',
      dataIndex: 'urgency',
      key: 'urgency',
      width: 100,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/grc/moc/${record.id}`)}>
          Open
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space align="start" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
          <Title level={4} style={{ margin: 0 }}>
            Management of Change (MOC)
          </Title>
          <Space wrap>
            <Link to="/home/approvals">Approval inbox</Link>
            <Link to="/grc/moc/reports">MOC reports</Link>
            <Button icon={<SyncOutlined />} onClick={() => void load()} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/grc/moc/new')}>
              New MOC
            </Button>
          </Space>
        </Space>

        <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
            <Segmented<ListingPreset>
              size="small"
              options={[...REGISTER_PRESETS]}
              value={listingPreset}
              onChange={(v) => setListingPreset(v)}
            />
          </div>
          <Input.Search
            allowClear
            placeholder="Search by title or MOC number"
            style={{ maxWidth: 400 }}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onSearch={(v) => {
              setAppliedSearch((v || '').trim());
            }}
            enterButton
          />
        </Space>

        <Table<MocHeader>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          onRow={(record) => ({
            onDoubleClick: () => navigate(`/grc/moc/${record.id}`),
          })}
        />
      </Card>
    </div>
  );
};

export default MOCRegisterPage;
